'use client';

import {
  Children,
  cloneElement,
  Fragment,
  HTMLAttributes,
  MouseEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useId,
  useState,
  forwardRef,
  ForwardedRef,
} from 'react';
import classNames from 'classnames';

import './css/Tabs.scss';
import { TabList } from './TabList';

export type TabDataItem = {
  button: ReactElement<HTMLButtonElement>;
  panel: ReactElement<HTMLDivElement>;
};
export type TabData = Array<TabDataItem>;

export interface TabsProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  iconAlignment?: 'left' | 'right';
  labelledBy?: string;
  onTabChange?: (selectedIndex: number) => void;
  previousTabAriaLabel?: string;
  nextTabAriaLabel?: string;
}

export const Tabs = forwardRef(
  (
    {
      children,
      className,
      iconAlignment = 'left',
      labelledBy,
      onTabChange,
      previousTabAriaLabel,
      nextTabAriaLabel,
      ...spreadProps
    }: TabsProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    let defaultSelectedTab = 0;
    Children.map(children, (child, index) => {
      // @ts-expect-error - review this later for an appropriate type
      if ((child as ReactElement).props.selected) {
        defaultSelectedTab = index;
      }
    });

    const [selectedIndex, setSelectedIndex] =
      useState<number>(defaultSelectedTab);

    const childCount = Children.count(children);
    useEffect(() => {
      if (selectedIndex >= childCount && childCount > 0) {
        setSelectedIndex(0);
      }
    }, [childCount, selectedIndex]);

    const handleTabClick = (e: MouseEvent) => {
      const tabButton = e.currentTarget as HTMLButtonElement;
      const newSelectedIndex = parseInt(tabButton.dataset.index || '0');
      setSelectedIndex(newSelectedIndex);
      onTabChange?.(newSelectedIndex);
    };

    // data structure to associate each tab button with its panels
    // @ts-expect-error - fix later
    const tabData: TabData =
      Children.map(children, (child, index) => {
        const childElement = child as ReactElement<{
          label: string;
          id?: string;
          renderIcon?: ReactNode;
        }>;
        const {
          label,
          // TODO: React Hook "useId" cannot be called inside a callback. React Hooks must be called in a React function component or a custom React Hook function. This could cause instability.
          // eslint-disable-next-line react-hooks/rules-of-hooks
          id = useId(),
          renderIcon,
        } = childElement.props;

        const isSelectedIndex = index === selectedIndex;

        const button = (
          <button
            id={`button-for-${id}`}
            onClick={handleTabClick}
            role="tab"
            aria-controls={id}
            aria-selected={isSelectedIndex ? 'true' : 'false'}
            tabIndex={isSelectedIndex ? undefined : -1}
            data-index={index}
          >
            {renderIcon && iconAlignment === 'left' && (
              <span className="ps-tabs-icon">{renderIcon}</span>
            )}
            <span className="ps-tabs-label">{label}</span>
            {renderIcon && iconAlignment === 'right' && (
              <span className="ps-tabs-icon">{renderIcon}</span>
            )}
          </button>
        );

        const panel = cloneElement(child as ReactElement<HTMLDivElement>, {
          'role': 'tabpanel',
          id,
          // @ts-expect-error - ts is not recognizing this attribute on the child element
          'selected': isSelectedIndex,
          'aria-labelledby': `button-for-${id}`,
        });

        return { button, panel };
      }) || [];

    return (
      <div
        {...spreadProps}
        className={classNames({
          'ps-tabs': true,
          'ps-tabs-align-icon-right': iconAlignment === 'right',
          [`${className}`]: className,
        })}
        ref={ref}
      >
        <TabList
          tabData={tabData}
          labelledBy={labelledBy}
          previousTabAriaLabel={previousTabAriaLabel}
          nextTabAriaLabel={nextTabAriaLabel}
        />
        <div className="ps-tabs-tabpanels">
          {tabData?.map((data: TabDataItem) => (
            <Fragment key={data.panel.props.id}>{data.panel}</Fragment>
          ))}
        </div>
      </div>
    );
  }
);
